import React, {Reducer, useReducer, useRef} from 'react'

import _ from 'lodash'

import LoginActions from '../../../../actions/LoginActions.js'
import reducer from '../../../../helpers/reducer.js'

import {Alert, IconButton, H3, H5, H6, P, Info, Button, Divider, Popup, S2} from '../../../../components/UI/index.js'
import {formSpacing, CurrencyInput, FormStack, MetricInput, Toggle, Input, Select} from '../../../../components/UI/forms/index.js'
import {Customer} from '../../../../../server/functions/customers/customers.types.js'
import {Reseller} from '../../../../../server/functions/resellers/resellers.types.js'
import {emit} from 'src/socketio/socketEmit.js'
import carriers from 'server/functions/parcels/carriers.js'
import Countries from 'src/utils/Countries.js'

interface LightspeedSettingsProps {
    user: Customer
    onChange: (user: Customer, save?: boolean) => void
    reseller: Reseller
    location: any
}

interface LightspeedSettingsState {
    shippingmethodsLoading?: boolean
    error?: string
    loading?: boolean
    verified?: boolean
    externalServiceLoading?: boolean
}

const initialState: LightspeedSettingsState = {
    shippingmethodsLoading: false,
    error: '',
    loading: false,
    verified: false,
    externalServiceLoading: false
}

export default function LightspeedSettings({user, onChange, reseller, location}: LightspeedSettingsProps) {
    const [{shippingmethodsLoading, externalServiceLoading, loading, verified, error}, dispatch] = useReducer<Reducer<LightspeedSettingsState, Partial<LightspeedSettingsState>>>(reducer, initialState)
    const popupRef = useRef<any>(null)
    const MAX_SHIPMENT_METHOD_WEIGHT = 999_999_000

    function parseShipmentMethodPriceScales(scalesInput: {fromWeight?: number, toWeight?: number, priceIncl?: string}[] = []) {
        const filteredScales = (scalesInput || []).filter((scale) => !!scale).map((scale) => {
            return {
                fromWeight: Math.max(0, Math.round(Number(scale.fromWeight) || 0)),
                toWeight: Math.max(0, Math.round(Number(scale.toWeight) || 0)),
                priceIncl: scale.priceIncl || '0'
            }
        }).sort((a, b) => a.fromWeight - b.fromWeight)

        filteredScales.forEach((scale, index) => {
            if (index === 0) {
                scale.fromWeight = 0
            } else {
                scale.fromWeight = filteredScales[index - 1].toWeight + 1
            }

            if (scale.toWeight < scale.fromWeight) {
                scale.toWeight = scale.fromWeight
            }
        })

        if (filteredScales.length > 0) {
            filteredScales[filteredScales.length - 1].toWeight = MAX_SHIPMENT_METHOD_WEIGHT
        }

        return filteredScales
    }

    function setShipmentMethodPriceScales(scalesInput: {fromWeight?: number, toWeight?: number, priceIncl?: string}[], save = false) {
        user.settings.webshops.lightspeed.shipmentMethodPriceScales = parseShipmentMethodPriceScales(scalesInput)
        onChange(user, save)
    }

    async function openPickupPointPricePopup() {
        const latestUser = await new Promise<Customer>((resolve, reject) => {
            LoginActions.getAccount(async (err, latestUser: Customer) => {
                if (err || !latestUser) {
                    reject(err || 'Kon klantinstellingen niet opnieuw laden. Probeer het opnieuw.')
                    return
                }
                resolve(latestUser)
            })
        })

        user.settings.webshops.lightspeed.externalServiceShippingPickupPointsId = latestUser.settings.webshops.lightspeed.externalServiceShippingPickupPointsId

        const currentScales = latestUser.settings.webshops.lightspeed.shipmentMethodPriceScales || []
        const hasScales = currentScales.length > 0

        if (!hasScales) {
            popupRef.current?.setInputs([
                {
                    type: 'metric',
                    name: 'fromWeight',
                    label: 'Vanaf gewicht',
                    value: 0,
                    min: 0,
                    max: MAX_SHIPMENT_METHOD_WEIGHT,
                    divisionFactor: 1_000,
                    append: 'kg'
                },
                {
                    type: 'metric',
                    name: 'toWeight',
                    label: 'Tot gewicht',
                    value: MAX_SHIPMENT_METHOD_WEIGHT,
                    min: 0,
                    max: MAX_SHIPMENT_METHOD_WEIGHT,
                    divisionFactor: 1_000,
                    append: 'kg'
                },
                {
                    type: 'currency',
                    name: 'priceIncl',
                    label: 'Prijs (incl. btw)',
                    value: '0'
                }
            ])

            popupRef.current?.open(
                'Afhaalpunten geactiveerd',
                'Stel direct een eerste gewichtsklasse met prijs in.',
                (values: {fromWeight: string | number, toWeight: string | number, priceIncl: string}) => {
                    setShipmentMethodPriceScales([{
                        fromWeight: Math.max(0, Number(values.fromWeight) || 0),
                        toWeight: Math.max(0, Number(values.toWeight) || 0),
                        priceIncl: values.priceIncl || '0'
                    }], true)

                    popupRef.current?.close()
                }
            )
        } else {
            setShipmentMethodPriceScales(currentScales)
            alert('Afhaalpunten geactiveerd')
        }
    }

    function onChangeChecked(key, event) {
        user.settings.webshops.lightspeed[key] = event.target.checked

        onChange(user)
    }

    function onChangeValue(key: string, event: {target: {value: string}}, parser?: (value: any) => any) {
        if (typeof parser === 'function') {
            user.settings.webshops.lightspeed[key] = parser(event.target.value)
        } else {
            user.settings.webshops.lightspeed[key] = event.target.value
        }

        onChange(user)
    }

    function onChangeValues(key, event) {
        user.settings.webshops.lightspeed[key] = event.target.values

        onChange(user)
    }

    function onAddShipmentMethodPriceScale() {
        const currentScales = parseShipmentMethodPriceScales(user.settings.webshops.lightspeed.shipmentMethodPriceScales || [])

        if (!currentScales.length) {
            setShipmentMethodPriceScales([{fromWeight: 0, toWeight: MAX_SHIPMENT_METHOD_WEIGHT, priceIncl: '0'}])
            return
        }

        const lastScale = currentScales[currentScales.length - 1]

        if (lastScale.fromWeight >= MAX_SHIPMENT_METHOD_WEIGHT) {
            dispatch({error: 'Geen extra gewichtsklasse mogelijk. De laatste gewichtsklasse heeft al het maximale gewicht.'})
            return
        }

        const nextFromWeight = Math.min(MAX_SHIPMENT_METHOD_WEIGHT, lastScale.fromWeight + 1_000)

        const updatedScales = [...currentScales]
        updatedScales[updatedScales.length - 1] = {
            ...lastScale,
            toWeight: Math.max(lastScale.fromWeight, nextFromWeight - 1)
        }

        updatedScales.push({
            fromWeight: nextFromWeight,
            toWeight: MAX_SHIPMENT_METHOD_WEIGHT,
            priceIncl: '0'
        })

        setShipmentMethodPriceScales(updatedScales)
    }

    function onRemoveShipmentMethodPriceScale(index: number) {
        const currentScales = [...(user.settings.webshops.lightspeed.shipmentMethodPriceScales || [])]
        currentScales.splice(index, 1)

        setShipmentMethodPriceScales(currentScales)
    }

    function onChangeShipmentMethodPriceScale(index: number, key: 'fromWeight' | 'toWeight' | 'priceIncl', event: any, parser?: (value: string) => number) {
        const currentScales = [...(user.settings.webshops.lightspeed.shipmentMethodPriceScales || [])]

        if (!currentScales[index]) {
            currentScales[index] = {fromWeight: 0, toWeight: 0, priceIncl: '0'}
        }

        const scale = currentScales[index]

        if (key === 'priceIncl') {
            scale.priceIncl = event.target.value
        } else {
            const parsedValue = typeof parser === 'function' ? parser(event.target.value) : parseFloat(event.target.value) || 0

            if (key === 'fromWeight') {
                scale.fromWeight = parsedValue
            }

            if (key === 'toWeight') {
                scale.toWeight = parsedValue
            }
        }

        setShipmentMethodPriceScales(currentScales)
    }

    async function onClickActivatePickupPoints() {
        dispatch({externalServiceLoading: true, error: ''})

        try {
            await emit('customers.lightspeedCreateExternalServiceShipping')
            await openPickupPointPricePopup()
        } catch (err) {
            dispatch({externalServiceLoading: false, error: err instanceof Error ? err.message : err.toString()})
        } finally {
            dispatch({externalServiceLoading: false})
        }
    }

    async function onClickDeactivatePickupPoints() {
        dispatch({externalServiceLoading: true, error: ''})

        try {
            await emit('customers.lightspeedDeleteExternalServiceShipping')
            alert('Afhaalpunten gedeactiveerd')
        } catch (err) {
            dispatch({externalServiceLoading: false, error: err instanceof Error ? err.message : err.toString()})
        } finally {
            dispatch({externalServiceLoading: false})
        }
    }

    function onClickGetShippingMethods() {
        dispatch({shippingmethodsLoading: true, error: ''})

        LoginActions.getShippingMethods(user, (err, shippingMethods) => {
            if (!err) {
                const shippingMethodsNames = shippingMethods.lightspeed || []

                const oldShippingMethods = JSON.parse(JSON.stringify(user.settings.webshops.lightspeed.shippingMethods || []))
                user.settings.webshops.lightspeed.shippingMethods = []

                shippingMethodsNames.map((name) => {
                    const shippingMethod = _.find(oldShippingMethods, {name}) || {disabled: false, name, options: []}

                    shippingMethod.disabled = shippingMethod ? shippingMethod.disabled : false
                    shippingMethod.options = shippingMethod ? shippingMethod.options : []

                    user.settings.webshops.lightspeed.shippingMethods = user.settings.webshops.lightspeed.shippingMethods || []
                    user.settings.webshops.lightspeed.shippingMethods.push(shippingMethod)
                })

                onChange(user)
            }

            dispatch({shippingmethodsLoading: false, error: err})
        })
    }

    function onChangeShippingMethodDisabled(index) {
        if (user.settings.webshops.lightspeed.shippingMethods) {
            user.settings.webshops.lightspeed.shippingMethods[index].disabled = !user.settings.webshops.lightspeed.shippingMethods[index].disabled

            onChange(user)
        }
    }

    function onChangeShippingMethod(index, event) {
        if (user.settings.webshops.lightspeed.shippingMethods) {
            user.settings.webshops.lightspeed.shippingMethods[index].options = event.target.values

            onChange(user)
        }
    }

    function onVerifyConnection() {
        dispatch({loading: true, verified: false, error: ''})

        onChange(user, true)

        setTimeout(() => { // wait for saving
            LoginActions.verifyWebshop('lightspeed', (err) => {
                if (err) {
                    dispatch({loading: false, error: `Verificatie niet succesvol, controleer de ingevulde gegevens. Foutmelding: ${err}`})
                } else {
                    dispatch({loading: false, verified: true, error: ''})
                }
            })
        }, 1000)
    }

    const sortedEnabledOptions = (_.keys(reseller.settings.parcels.products) || []).filter((product) => {
        return _.some(_.keys(reseller.settings.parcels.products[product]), (carrier) => {
            return reseller.settings.parcels.products[product][carrier]
        })
    }).sort()

    const enabledOptions = sortedEnabledOptions.map((option) => {
        return {value: option, title: option}
    })

    const shipmentMethodAllowedCountriesOptions = Object.values(carriers).reduce<string[]>((prev, current) => {
        if (current.products['Afhaalpunt']) {
            current.products['Afhaalpunt'].countries.forEach((country) => {
                if (!prev.includes(country)) {
                    prev.push(country)
                }
            })
        }

        return prev
    }, []).map((country) => {
        return {value: country, title: Countries.find((c) => c.code2 === country)?.name || country}
    })

    const shipmentMethodPriceScales = parseShipmentMethodPriceScales(user.settings.webshops.lightspeed.shipmentMethodPriceScales || [])

    return (
        <div>
            {location.state && location.state.message &&
                <Alert style={{marginBottom: 6}} variant='success'>{location.state.message}</Alert>
            }
            {location.state && location.state.err &&
                <Alert style={{marginBottom: 6}} variant='error'>{`Lightspeed foutmelding: ${location.state.err}`}</Alert>
            }

            <H3 style={{marginBottom: 36}}>Lightspeed</H3>

            <H5 style={{marginBottom: 24}}>Instellingen</H5>

            <FormStack style={{marginBottom: formSpacing.rowGap}}>
                <Toggle
                    label='Actief'
                    checked={user.settings.webshops.lightspeed.active}
                    onChange={(event) => onChangeChecked('active', event)}
                />
            </FormStack>

            {user.settings.webshops.lightspeed.active &&
                <>
                    <FormStack style={{marginBottom: formSpacing.rowGap}}>
                        <Select
                            label='Webshop taal'
                            value={user.settings.webshops.lightspeed.language}
                            options={[{title: 'Nederlands', value: 'nl'}, {title: 'Engels', value: 'en'}]}
                            onChange={(event) => onChangeValue('language', event)}
                        />

                        <Select
                            label='Cluster id'
                            info='Op te vragen bij de Lightspeed klantenservice.'
                            options={[{title: 'EU1', value: 'webshopapp'}, {title: 'US1', value: 'shoplightspeed'}]}
                            value={user.settings.webshops.lightspeed.clusterId}
                            onChange={(event) => onChangeValue('clusterId', event)}
                        />

                        <Input
                            label='API key'
                            info='Op te vragen bij de Lightspeed klantenservice.'
                            value={user.settings.webshops.lightspeed.apiKey}
                            onChange={(event) => onChangeValue('apiKey', event)}
                        />

                        <Input
                            label='API secret'
                            info='Op te vragen bij de Lightspeed klantenservice.'
                            value={user.settings.webshops.lightspeed.apiSecret}
                            onChange={(event) => onChangeValue('apiSecret', event)}
                        />

                        <Input
                            type='number'
                            min={1}
                            max={180}
                            label='Aantal dagen terugkijken voor ophalen bestellingen'
                            value={user.settings.webshops.lightspeed.nrOfDays || 7}
                            onChange={(event) => onChangeValue('nrOfDays', event, parseInt)}
                        />

                        <Select
                            label='Update bestelling status in Lightspeed'
                            info=''
                            value={user.settings.webshops.lightspeed.updateStatus}
                            onChange={(event) => onChangeValue('updateStatus', event)}
                        >
                            <option value=''>Nooit</option>
                            <option value='confirm'>Na bevestigen</option>
                            <option value='afterSent'>Na verzenden</option>
                        </Select>

                        <Toggle
                            label='Alleen betaalde bestellingen importeren'
                            checked={user.settings.webshops.lightspeed.ignoreNotPayedOrders}
                            onChange={(event) => onChangeChecked('ignoreNotPayedOrders', event)}
                        />

                        <Toggle
                            label='Consument opmerking meegeven als bezorginstructie'
                            checked={user.settings.webshops.lightspeed.useComment}
                            onChange={(event) => onChangeChecked('useComment', event)}
                        />
                    </FormStack>

                    <br />
                    <br />
                    <H6 style={{marginBottom: 12}}>Afhaalpunten</H6>
                    <Divider style={{marginBottom: 12}} />

                    <P style={{marginBottom: 12}}>Kies hier om het bezorgen van zendingen bij een afhaalpunt mogelijk te maken voor klanten in de checkout. De optie is direct na activatie zichtbaar. Klanten kunnen kiezen uit alle beschikbare afhaalpunten. Let op, dit wordt alleen ondersteund bij de <a href='https://ecom-support.lightspeedhq.com/hc/en-us/articles/4406352045083-About-the-new-eCom-checkout' target='_blank' rel='noreferrer'>nieuwe eCom checkout</a>.</P>

                    {user.settings.webshops.lightspeed.externalServiceShippingPickupPointsId &&
                        <P style={{marginBottom: 12}}>Afhaalpunten zijn geactiveerd.</P>
                    }

                    <div style={{display: 'flex', columnGap: formSpacing.columnGap}}>
                        <Button loading={externalServiceLoading} onClick={onClickActivatePickupPoints}>Activeer afhaalpunten in Lightspeed</Button>

                        {user.settings.webshops.lightspeed.externalServiceShippingPickupPointsId && (
                            <Button loading={externalServiceLoading} onClick={onClickDeactivatePickupPoints}>Deactiveer afhaalpunten</Button>
                        )}
                    </div>

                    {user.settings.webshops.lightspeed.externalServiceShippingPickupPointsId && (
                        <>
                            <FormStack style={{marginTop: formSpacing.section, marginBottom: formSpacing.rowGap}}>
                                <Input
                                    label='Titel afhaalpunt'
                                    info='Deze titel wordt getoond in de Lightspeed checkout.'
                                    value={user.settings.webshops.lightspeed.shipmentMethodTitle || 'Bezorg bij afhaalpunt'}
                                    onChange={(event) => onChangeValue('shipmentMethodTitle', event)}
                                />

                                <Select
                                    noSort
                                    label='Toegestane landen'
                                    options={shipmentMethodAllowedCountriesOptions}
                                    values={user.settings.webshops.lightspeed.shipmentMethodAllowedCountries || []}
                                    onChange={(event) => onChangeValues('shipmentMethodAllowedCountries', event)}
                                />

                                <CurrencyInput
                                    label='Minimumorderbedrag voor gratis verzending (incl. btw)'
                                    info='Vanaf dit orderbedrag worden de verzendkosten naar een afhaalpunt gratis.'
                                    value={user.settings.webshops.lightspeed.shipmentMethodFreeFromOrderValue || ''}
                                    onChange={(event) => {
                                        if (event.target.value === '0,00') {
                                            event.target.value = ''
                                        }
                                        onChangeValue('shipmentMethodFreeFromOrderValue', event)
                                    }}
                                />
                            </FormStack>

                            <H6 style={{marginTop: 24, marginBottom: 0}}>Tarieven voor afhaalpunten</H6>
                            <S2 style={{marginBottom: 12}}>Stel verzendkosten om te bepalen hoeveel je klanten zullen gaan betalen. Je kunt diverse tarieven instellen voor verschillende pakketgewichtsklassen.</S2>

                            {shipmentMethodPriceScales.map((scale, index) => {
                                const isLastScale = index === shipmentMethodPriceScales.length - 1

                                return (
                                    <div key={`shipmentMethodPriceScale${index}`} style={{display: 'flex', alignItems: 'center', columnGap: formSpacing.columnGap, marginBottom: 12}}>
                                        <MetricInput
                                            min={0}
                                            label='Vanaf gewicht'
                                            max={MAX_SHIPMENT_METHOD_WEIGHT}
                                            divisionFactor={1000}
                                            value={scale.fromWeight ?? 0}
                                            append='kg'
                                            disabled
                                        />

                                        <MetricInput
                                            min={0}
                                            label='Tot gewicht'
                                            max={MAX_SHIPMENT_METHOD_WEIGHT}
                                            divisionFactor={1000}
                                            value={scale.toWeight ?? 0}
                                            append='kg'
                                            disabled={isLastScale}
                                            info={isLastScale ? 'Laatste gewichtsklasse is altijd open en loopt automatisch door naar oneindig.' : ''}
                                            onChange={(event) => onChangeShipmentMethodPriceScale(index, 'toWeight', event, (value) => parseFloat(value) || 0)}
                                        />

                                        <CurrencyInput
                                            label='Prijs (incl. btw)'
                                            value={scale.priceIncl || '0'}
                                            onChange={(event) => onChangeShipmentMethodPriceScale(index, 'priceIncl', event)}
                                        />

                                        <IconButton
                                            icon='mdi mdi-delete'
                                            onClick={() => onRemoveShipmentMethodPriceScale(index)}
                                        />
                                    </div>
                                )
                            })}

                            <Button variant='outline' onClick={onAddShipmentMethodPriceScale}>Gewichtsklasse toevoegen</Button>
                        </>
                    )}

                    <br />
                    <br />
                    <H6 style={{marginBottom: 12}}>Verzendmethodes</H6>
                    <Divider style={{marginBottom: 12}} />

                    <div style={{display: 'flex', alignItems: 'center', marginBottom: 12}}>
                        <P style={{display: 'flex', flex: 1}}>Verzendmethodes ophalen uit Lightspeed
                            <Info style={{marginLeft: 6}} text='Klik op de knop om verzendmethodes uit Lightspeed op te halen. Selecteer vervolgens voor elke verzendmethode de nodige verzendopties.' />
                        </P>

                        <IconButton loading={shippingmethodsLoading} onClick={onClickGetShippingMethods}>
                            <i className='mdi mdi-refresh' />
                        </IconButton>
                    </div>

                    {(user.settings.webshops.lightspeed.shippingMethods || []).map((shippingmethod, index) => {
                        return (
                            <div key={`shippingmethod${index}`} style={{display: 'flex', alignItems: 'center', columnGap: formSpacing.columnGap, marginBottom: 12}}>
                                <Toggle
                                    checked={!shippingmethod.disabled}
                                    onChange={() => onChangeShippingMethodDisabled(index)}
                                />

                                <P style={{marginLeft: 14, flex: 1}}>{shippingmethod.name}</P>

                                <Select
                                    noSort
                                    label='Verzendopties'
                                    values={shippingmethod.options || []}
                                    options={enabledOptions}
                                    onChange={(event) => onChangeShippingMethod(index, event)}
                                />
                            </div>
                        )
                    })}

                    <FormStack>
                        <Button
                            variant='text'
                            loading={loading}
                            onClick={onVerifyConnection}
                        >
                            Verifieer
                        </Button>

                        {error && (
                            <Alert style={{marginBottom: 6}} onClose={() => dispatch({error: ''})} variant='error'>{error}</Alert>
                        )}

                        {!error && verified && (
                            <Alert style={{marginBottom: 6}} onClose={() => dispatch({verified: false})}>Verificatie succesvol.</Alert>
                        )}
                    </FormStack>
                </>
            }

            <Popup ref={popupRef} />
        </div>
    )
}
